import { NavLink, Outlet, useNavigate } from 'react-router-dom'
import {
  Users, Monitor, Key, ShieldAlert, Ticket,
  Activity, Settings, LogOut, Search, Menu, X, BookOpen, Globe, ScrollText, BarChart2, LayoutDashboard, LayoutTemplate, Building2, Sparkles,
} from 'lucide-react'
import { useMsal } from '@azure/msal-react'
import { PrismLogo } from '../ui/PrismLogo'
import { ThemeToggle } from '../theme/ThemeToggle'
import { useGraphPhoto } from '../../hooks/useGraphPhoto'
import { useCurrentUser } from '../../hooks/useCurrentUser'
import { useState, useEffect, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { apiFetch } from '../../lib/api'
import type { ApiResponse, IdentitySummary, PermissionPage } from '@portico/types'

const navItems: {
  to: string
  label: string
  icon: typeof LayoutDashboard
  page: PermissionPage
}[] = [
  { to: '/',                label: 'Overview',         icon: LayoutDashboard, page: 'overview' },
  { to: '/identities',      label: 'People',          icon: Users,           page: 'identities' },
  { to: '/organization',    label: 'Organization',    icon: Building2,       page: 'organization' },
  { to: '/devices',         label: 'Devices',         icon: Monitor,         page: 'devices' },
  { to: '/licenses',        label: 'Licenses',        icon: Key,             page: 'licenses' },
  { to: '/license-catalog', label: 'License Catalog', icon: BookOpen,        page: 'license_catalog' },
  { to: '/services',          label: 'Services',         icon: Globe,           page: 'services' },
  { to: '/service-profiles',  label: 'Service Profiles',  icon: LayoutTemplate,  page: 'services' },
  { to: '/tickets',          label: 'Tickets',          icon: Ticket,          page: 'tickets' },
  { to: '/security',        label: 'Security',        icon: ShieldAlert,     page: 'security' },
  { to: '/reports',         label: 'Reports',         icon: BarChart2,       page: 'reports' },
  { to: '/ai-usage',        label: 'AI Usage',        icon: Sparkles,        page: 'ai_usage' },
  { to: '/sync',            label: 'Integrations',    icon: Activity,        page: 'sync' },
  { to: '/logs',            label: 'PRISM Logs',      icon: ScrollText,      page: 'logs' },
]

function getInitials(name: string): string {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('')
}

// ─── People Search Modal ──────────────────────────────────────────────────────

function PeopleSearchModal({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate()
  const [query, setQuery] = useState('')
  const [selected, setSelected] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)

  const { data, isFetching } = useQuery({
    queryKey: ['people-search', query],
    queryFn: () =>
      apiFetch<ApiResponse<IdentitySummary[]>>(
        `/api/v1/identities?search=${encodeURIComponent(query)}&limit=8&sortBy=displayName&sortDir=asc`
      ),
    enabled: query.trim().length > 0,
    staleTime: 10_000,
  })

  const results = data?.data ?? []

  // Focus input on mount
  useEffect(() => { inputRef.current?.focus() }, [])

  // Reset selection when results change
  useEffect(() => { setSelected(0) }, [results.length])

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Escape') { onClose(); return }
    if (e.key === 'ArrowDown') { e.preventDefault(); setSelected((s) => Math.min(s + 1, results.length - 1)) }
    if (e.key === 'ArrowUp') { e.preventDefault(); setSelected((s) => Math.max(s - 1, 0)) }
    if (e.key === 'Enter' && results[selected]) {
      navigate(`/identities/${results[selected].id}`)
      onClose()
    }
  }

  function handleSelect(id: string) {
    navigate(`/identities/${id}`)
    onClose()
  }

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0, zIndex: 999,
          backgroundColor: 'rgba(0,0,0,0.45)',
          backdropFilter: 'blur(2px)',
        }}
      />

      {/* Modal */}
      <div style={{
        position: 'fixed', top: '15%', left: '50%', transform: 'translateX(-50%)',
        zIndex: 1000, width: '100%', maxWidth: '480px', padding: '0 16px',
      }}>
        <div style={{
          backgroundColor: 'var(--bg-surface)',
          border: '1px solid var(--border-default)',
          borderRadius: '12px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          overflow: 'hidden',
        }}>
          {/* Input row */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 16px', borderBottom: results.length > 0 || isFetching ? '1px solid var(--border-subtle)' : 'none' }}>
            <Search size={15} style={{ color: 'var(--text-tertiary)', flexShrink: 0 }} />
            <input
              ref={inputRef}
              type="text"
              placeholder="Search people by name, email or job title…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              style={{
                flex: 1, border: 'none', outline: 'none',
                backgroundColor: 'transparent',
                color: 'var(--text-primary)', fontSize: '14px',
              }}
            />
            {isFetching && <div style={{ width: '14px', height: '14px', borderRadius: '50%', border: '2px solid var(--border-default)', borderTopColor: 'var(--brand-coral)', animation: 'spin 0.7s linear infinite', flexShrink: 0 }} />}
            {!isFetching && query && <button onClick={() => setQuery('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-tertiary)', display: 'flex', padding: 0 }}><X size={13} /></button>}
          </div>

          {/* Results */}
          {results.length > 0 && (
            <div style={{ maxHeight: '340px', overflowY: 'auto' }}>
              {results.map((person, i) => (
                <button
                  key={person.id}
                  onClick={() => handleSelect(person.id)}
                  onMouseEnter={() => setSelected(i)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '12px',
                    width: '100%', padding: '10px 16px', border: 'none', textAlign: 'left', cursor: 'pointer',
                    backgroundColor: i === selected ? 'var(--interactive-secondary-hover)' : 'transparent',
                    borderBottom: i < results.length - 1 ? '1px solid var(--border-subtle)' : 'none',
                  }}
                >
                  {/* Initials avatar */}
                  <div style={{
                    width: '32px', height: '32px', borderRadius: '50%', flexShrink: 0,
                    backgroundColor: 'var(--brand-coral)', opacity: 0.85,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '11px', fontWeight: 600, color: 'white', letterSpacing: '0.03em',
                  }}>
                    {person.displayName.split(' ').filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase() ?? '').join('')}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ margin: 0, fontSize: '13px', fontWeight: 500, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {person.displayName}
                    </p>
                    <p style={{ margin: 0, fontSize: '11px', color: 'var(--text-tertiary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {person.primaryEmail}{person.jobTitle ? ` · ${person.jobTitle}` : ''}
                    </p>
                  </div>
                  <span style={{
                    fontSize: '10px', padding: '2px 6px', borderRadius: '4px', flexShrink: 0,
                    backgroundColor: person.accountStatus === 'ACTIVE' ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.08)',
                    color: person.accountStatus === 'ACTIVE' ? '#16a34a' : '#dc2626',
                    fontWeight: 500,
                  }}>
                    {person.accountStatus === 'ACTIVE' ? 'Active' : 'Inactive'}
                  </span>
                </button>
              ))}
            </div>
          )}

          {/* Empty state */}
          {query.trim().length > 0 && !isFetching && results.length === 0 && (
            <p style={{ margin: 0, padding: '20px 16px', fontSize: '13px', color: 'var(--text-tertiary)', textAlign: 'center' }}>
              No people found for "{query}"
            </p>
          )}

          {/* Hint */}
          <div style={{ padding: '8px 16px', borderTop: results.length > 0 ? '1px solid var(--border-subtle)' : 'none', display: 'flex', gap: '12px' }}>
            <span style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>↑↓ navigate</span>
            <span style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>↵ open</span>
            <span style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>esc close</span>
          </div>
        </div>
      </div>
    </>
  )
}

// ─── App Layout ───────────────────────────────────────────────────────────────

export function AppLayout() {
  const { instance, accounts } = useMsal()
  const navigate = useNavigate()
  const [searchOpen, setSearchOpen] = useState(false)
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const photoUrl = useGraphPhoto()
  const { canView, isLoading: permissionsLoading, user } = useCurrentUser()

  // Filter nav items by permission — show all while loading to prevent flash
  const visibleNavItems = permissionsLoading
    ? navItems
    : navItems.filter((item) => canView(item.page))

  const account = accounts[0]
  // No MSAL account under local dev sign-in (ADR-017): fall back to /auth/me.
  const displayName = account?.name ?? account?.username ?? user?.displayName ?? 'Unknown'
  const email = account?.username ?? user?.email ?? ''
  const initials = getInitials(displayName)

  const handleSignOut = async () => {
    await instance.logoutRedirect()
  }

  const closeSidebar = () => setSidebarOpen(false)

  // Global Cmd/Ctrl+K shortcut
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setSearchOpen((o) => !o)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  return (
    <div className="app-layout" style={{ display: 'flex', height: '100dvh', overflow: 'hidden' }}>

      {searchOpen && <PeopleSearchModal onClose={() => setSearchOpen(false)} />}

      {/* ─── Mobile backdrop ─────────────────────────────────────────────────── */}
      {sidebarOpen && (
        <div className="app-sidebar-backdrop" onClick={closeSidebar} />
      )}

      {/* ─── Sidebar ─────────────────────────────────────────────────────────── */}
      <aside
        className={`app-sidebar${sidebarOpen ? ' sidebar-open' : ''}`}
        style={{
          width: '220px',
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: 'var(--sidebar-bg)',
          borderRight: '1px solid var(--sidebar-border)',
          overflow: 'hidden',
        }}
      >
        {/* Logo + mobile close */}
        <div style={{
          padding: '20px 16px 16px',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '8px',
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <NavLink to="/" style={{ textDecoration: 'none' }}>
              <PrismLogo size="sm" variant="full" />
            </NavLink>
            <div style={{ marginTop: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <p style={{
                fontSize: '10px',
                color: 'var(--sidebar-text)',
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                fontWeight: 500,
                margin: 0,
              }}>
                IT Visibility Platform
              </p>
              {import.meta.env.VITE_APP_ENV !== 'production' && (
                <span style={{
                  fontSize: '9px',
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: '#f59e0b',
                  background: 'rgba(245,158,11,0.12)',
                  border: '1px solid rgba(245,158,11,0.3)',
                  borderRadius: '3px',
                  padding: '1px 5px',
                  lineHeight: '14px',
                }}>
                  {import.meta.env.VITE_APP_ENV ?? 'dev'}
                </span>
              )}
            </div>
          </div>
          {/* Mobile-only close button (hidden on desktop via CSS) */}
          <button
            onClick={closeSidebar}
            aria-label="Close menu"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '28px',
              height: '28px',
              borderRadius: '6px',
              border: 'none',
              background: 'transparent',
              color: 'var(--sidebar-text)',
              cursor: 'pointer',
              flexShrink: 0,
              marginTop: '2px',
            }}
            className="app-sidebar-close-btn"
          >
            <X size={15} strokeWidth={2} />
          </button>
        </div>

        {/* Search trigger */}
        <div style={{ padding: '12px 10px 4px' }}>
          <button
            onClick={() => setSearchOpen(true)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              width: '100%',
              padding: '7px 10px',
              borderRadius: '6px',
              border: '1px solid var(--sidebar-border)',
              backgroundColor: 'var(--sidebar-search-bg)',
              color: 'var(--sidebar-text)',
              cursor: 'pointer',
              fontSize: '12px',
            }}
          >
            <Search size={13} strokeWidth={2} />
            <span style={{ flex: 1, textAlign: 'left' }}>Search people...</span>
          </button>
        </div>

        {/* Navigation */}
        <nav style={{ flex: 1, padding: '8px 10px', overflow: 'auto' }}>
          <p style={{
            padding: '8px 8px 4px',
            fontSize: '10px',
            fontWeight: 600,
            color: 'var(--sidebar-text)',
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
          }}>
            Platform
          </p>

          {visibleNavItems.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              onClick={closeSidebar}
              style={({ isActive }) => ({
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '7px 10px',
                borderRadius: '6px',
                marginBottom: '1px',
                fontSize: '13px',
                fontWeight: isActive ? 500 : 400,
                color: isActive ? 'var(--sidebar-active-text)' : 'var(--sidebar-text)',
                backgroundColor: isActive ? 'var(--sidebar-active-bg)' : 'transparent',
                textDecoration: 'none',
                transition: 'all 0.1s ease',
              })}
              onMouseEnter={(e) => {
                const el = e.currentTarget
                const isActive = el.getAttribute('aria-current') === 'page'
                if (!isActive) {
                  el.style.backgroundColor = 'var(--sidebar-hover-bg)'
                  el.style.color = 'var(--sidebar-text-hover)'
                }
              }}
              onMouseLeave={(e) => {
                const el = e.currentTarget
                const isActive = el.getAttribute('aria-current') === 'page'
                if (!isActive) {
                  el.style.backgroundColor = 'transparent'
                  el.style.color = 'var(--sidebar-text)'
                }
              }}
            >
              <Icon size={15} strokeWidth={1.75} />
              {label}
            </NavLink>
          ))}
        </nav>

        {/* ─── User profile footer ─────────────────────────────────────────── */}
        <div style={{
          padding: '10px',
          borderTop: '1px solid var(--sidebar-border)',
        }}>
          {/* User card */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '6px 4px 8px',
          }}>
            {/* Avatar — photo or initials fallback */}
            {photoUrl ? (
              <img
                src={photoUrl}
                alt={displayName}
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  objectFit: 'cover',
                  flexShrink: 0,
                  border: '1px solid var(--sidebar-border)',
                }}
              />
            ) : (
              <div style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                backgroundColor: 'var(--interactive-primary)',
                opacity: 0.9,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '12px',
                fontWeight: 600,
                color: 'white',
                flexShrink: 0,
                letterSpacing: '0.02em',
              }}>
                {initials}
              </div>
            )}

            {/* Name + email */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{
                margin: 0,
                fontSize: '12px',
                fontWeight: 500,
                color: 'var(--sidebar-text-hover)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                lineHeight: 1.4,
              }}>
                {displayName}
              </p>
              <p style={{
                margin: 0,
                fontSize: '11px',
                color: 'var(--sidebar-text)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                lineHeight: 1.4,
              }}>
                {email}
              </p>
            </div>

            {/* Sign out */}
            <button
              onClick={() => void handleSignOut()}
              title="Sign out"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '28px',
                height: '28px',
                borderRadius: '6px',
                border: 'none',
                background: 'transparent',
                color: 'var(--sidebar-text)',
                cursor: 'pointer',
                flexShrink: 0,
              }}
            >
              <LogOut size={14} strokeWidth={1.75} />
            </button>
          </div>

          {/* Controls row */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          }}>
            {canView('settings') && (
              <button
                onClick={() => navigate('/settings')}
                title="Settings"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '28px',
                  height: '28px',
                  borderRadius: '6px',
                  border: 'none',
                  background: 'transparent',
                  color: 'var(--sidebar-text)',
                  cursor: 'pointer',
                }}
              >
                <Settings size={14} strokeWidth={1.75} />
              </button>
            )}

            <ThemeToggle />
          </div>

          {/* Version */}
          <div
            title={`v${__APP_VERSION__} — ${__GIT_SHA__ === 'dev' ? 'local development' : __GIT_SHA__}`}
            style={{
              paddingTop: '6px',
              fontSize: '10px',
              color: 'var(--sidebar-text)',
              opacity: 0.4,
              textAlign: 'center',
              userSelect: 'none',
              cursor: 'default',
            }}
          >
            v{__APP_VERSION__}{__GIT_SHA__ !== 'dev' ? ` (${__GIT_SHA__.slice(0, 7)})` : ''}
          </div>
        </div>
      </aside>

      {/* ─── Main content ─────────────────────────────────────────────────────── */}
      <main style={{
        flex: 1,
        overflow: 'auto',
        backgroundColor: 'var(--bg-base)',
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
      }}>
        {/* Mobile top bar — visible only on small screens via CSS */}
        <div className="app-mobile-bar" style={{ display: 'none' }}>
          <button
            className="app-mobile-bar-hamburger"
            onClick={() => setSidebarOpen(true)}
            aria-label="Open menu"
          >
            <Menu size={18} strokeWidth={2} />
          </button>
          <PrismLogo size="sm" variant="full" />
        </div>

        <div style={{ flex: 1, overflow: 'auto' }}>
          <Outlet />
        </div>
      </main>
    </div>
  )
}
